import type { MutableRefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { ReactComponent as EditPencil } from "assets/images/icons/EditPencil.svg";
import { getAdressTypeOption, getCompanyAddressV2Details } from "api";
import { useGetAddressesV2 } from "api/queries/company/useGetAddressesV2";
import { CreateUpdateCompanyAddressV2ResponseType } from "api/response/company.response";
import usePermissions from "hooks/usePermissions";
import useFeatureFlag from "hooks/useFeatureFlag";
import { useGetAddressesV2Details } from "api/queries/company/useGetAddressV2Details";
import Button from "components/atoms/Button/Button";
import AddressV2Dialog from "components/organisms/AddressV2Dialog/AddressV2Dialog";
import testIds from "constants/testIds";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { StopAddressType } from "types/MultiStop";
import SearchSelect from "../../../Common/SearchSelect";
import { AddressOption, formatAddressV2 } from "../../PickupDeliveryAddress/helpers";
import useGetMultiStopAddressSelect from "../../hooks/useGetMultiStopAddressSelect";

export default function StopAddressForm({
  index,
  portLinkedTimezoneRef,
}: {
  index: number;
  portLinkedTimezoneRef?: MutableRefObject<string | null>;
}) {
  const intl = useIntl();
  const isOptionalPortAddressFormEnabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);
  const { permissions }: any = usePermissions();
  const addressBookPermissions = permissions ? JSON.parse(permissions).permissions.quoting : null;

  const [searchInput, setSearchInput] = useState("");
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(undefined);
  const { handleSelectAddress } = useGetMultiStopAddressSelect();

  const { getValues, setValue, watch } = useFormContext();

  const currentAddressValues = watch(`schedule.stops.${index}`) ?? {};
  const { address_reference_number, address_street_address, address_name } = currentAddressValues;
  const stopAddressType = watch(`schedule.stops.${index}.stop_address_type`);
  const stopAirportCode = watch(`schedule.stops.${index}.stop_airport_code`);
  const stopAirportName = watch(`schedule.stops.${index}.stop_airport_name`);
  const stopSeaportCode = watch(`schedule.stops.${index}.stop_seaport_code`);
  const stopSeaportName = watch(`schedule.stops.${index}.stop_seaport_name`);
  const isOptionalAirportOrSeaport =
    isOptionalPortAddressFormEnabled &&
    (stopAddressType === StopAddressType.AIRPORT || stopAddressType === StopAddressType.SEAPORT);

  const { data: addresses = [] } = useGetAddressesV2(
    {
      params: {
        query: searchInput,
        payment_location: getValues("organizational.location"),
      },
    },
    {
      keepPreviousData: true,
    },
  );

  const { data: addressV2Details, refetch: refetchAddresses } = useGetAddressesV2Details(address_reference_number, {
    options: {
      enabled: Boolean(!address_street_address && address_reference_number),
      refetchOnMount: true,
    },
  });

  useEffect(() => {
    if (!addressV2Details) return;
    const { timezone, ...rest } = addressV2Details;
    handleSelectAddress([rest], index);
  }, [addressV2Details]);

  const options =
    addressV2Details && isInitialRender
      ? [formatAddressV2(addressV2Details as any)]
      : addresses?.map((address: any) => formatAddressV2(address));

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  const previousAddressNameRef = useRef<string | undefined>(address_name);
  const onInputChange = (val: string) => {
    if (!val?.trim()) {
      setValue(`schedule.stops.${index}.address_id`, undefined);
      setValue(`schedule.stops.${index}.address_reference_number`, "");
    }
    if (timeoutId.current) {
      clearTimeout(timeoutId.current);
    }
    timeoutId.current = setTimeout(() => {
      setSearchInput(val);
      setIsInitialRender(false);
    }, 500);
  };

  const handleAddNewAddress = (data: CreateUpdateCompanyAddressV2ResponseType) => {
    if (!data) return;
    handleSelectAddress([data], index);
  };

  const handleEditAddress = async () => {
    const stop = getValues(`schedule.stops.${index}`) ?? {};
    const referenceNumber = getValues(`schedule.stops.${index}.address_reference_number`);
    const fallbackAddress = {
      ...stop,
      name: getValues(`schedule.stops.${index}.address_name`),
      reference_number: referenceNumber,
    };

    if (!referenceNumber) {
      setSelectedAddress(fallbackAddress);
      setShowAddressDialog(true);
      return;
    }

    try {
      const { data } = await getCompanyAddressV2Details(referenceNumber);
      const details: Record<string, unknown> = { ...data };
      ["city", "country", "state", "locode"].forEach((item) => {
        details[`${item}_id`] = details[`${item}_id`] ?? details[item];
      });
      setSelectedAddress(details);
    } catch {
      setSelectedAddress(fallbackAddress);
    }
    setShowAddressDialog(true);
  };

  // If an optional address is cleared, restore timezone from the selected airport/seaport.
  useEffect(() => {
    const previousAddressName = previousAddressNameRef.current;
    previousAddressNameRef.current = address_name;

    const addressWasCleared = !!previousAddressName && !address_name;
    if (!addressWasCleared) return;
    if (!isOptionalPortAddressFormEnabled) return;

    // Clearing the optional stop address input should also clear persisted
    // address identity fields in draft payload.
    setValue(`schedule.stops.${index}.address_id`, undefined);
    setValue(`schedule.stops.${index}.address_reference_number`, "");

    if (stopAddressType !== StopAddressType.AIRPORT && stopAddressType !== StopAddressType.SEAPORT) return;

    const cachedTz = portLinkedTimezoneRef?.current;
    if (cachedTz) {
      setValue(`schedule.stops.${index}.stop_timezone`, cachedTz, { shouldValidate: true });
      return;
    }

    const endpoint = stopAddressType === StopAddressType.AIRPORT ? "/common/airport" : "/common/seaport";
    const query =
      stopAddressType === StopAddressType.AIRPORT ? stopAirportCode || stopAirportName : stopSeaportCode || stopSeaportName;
    if (!query) return;

    getAdressTypeOption(endpoint, query).then((res) => {
      const linkedTimezone = res?.data?.results?.[0]?.address?.timezone;
      if (linkedTimezone) {
        setValue(`schedule.stops.${index}.stop_timezone`, linkedTimezone, { shouldValidate: true });
      }
    });
  }, [
    address_name,
    stopAddressType,
    stopAirportCode,
    stopAirportName,
    stopSeaportCode,
    stopSeaportName,
    index,
    setValue,
    portLinkedTimezoneRef,
    isOptionalPortAddressFormEnabled,
  ]);

  return (
    <div className="w-100" style={{ maxWidth: "450px" }}>
      <SearchSelect
        name={`schedule.stops.${index}.address_name`}
        testId={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_ADDRESS_SELECT_PREFIX}-${index}`}
        preselect={false}
        enableManualValue={false}
        required={!isOptionalAirportOrSeaport}
        disableAutoClear
        options={options}
        placeholder={intl.formatMessage({
          id: "new-request.schedule.multistop.searchAddress",
          defaultMessage: "Search address",
        })}
        // disable client side filtering
        filterBy={() => true}
        handleRenderOption={({ option }: any) => <AddressOption option={option} />}
        onInputChange={onInputChange}
        onSelect={(event: any[]) => handleSelectAddress(event, index)}
      />

      <div className="d-flex justify-content-between align-items-start mt-sm">
        {address_name ? (
          <div className="d-flex">
            <div className="text-2xs text-primary">
              <div className="text-secondary">{currentAddressValues?.address_name}</div>
              <div>{currentAddressValues?.street_address}</div>
              <div>{currentAddressValues?.street_address_line_two}</div>
              <div>
                {currentAddressValues?.city_name}
                {currentAddressValues?.state_name && `, ${currentAddressValues?.state_name}`}
                {currentAddressValues?.country_name && `, ${currentAddressValues?.country_name}`}{" "}
                {currentAddressValues?.zipcode}
              </div>
            </div>
            {addressBookPermissions?.allow_address_book_entry && (
              <div className="align-self-end ml-md cursor-pointer">
                <EditPencil onClick={handleEditAddress} />
              </div>
            )}
          </div>
        ) : (
          <div />
        )}

        {addressBookPermissions?.allow_address_book_entry && (
          <>
            <Button variant="link" className="p-xs" onClick={() => setShowAddressDialog(true)}>
              <span className="font-b4">
                + <FormattedMessage id="new-request.schedule.addNewAddress" defaultMessage="Add new address" />
              </span>
            </Button>
            <AddressV2Dialog
              onClose={() => {
                setShowAddressDialog(false);
                setSelectedAddress(undefined);
              }}
              isOpen={showAddressDialog}
              refetchAddresses={refetchAddresses}
              source="quick_quote_request"
              onAddNewAddress={handleAddNewAddress}
              formSelectedShipperLocation={getValues("organizational.location")}
              selectedAddress={selectedAddress}
            />
          </>
        )}
      </div>
    </div>
  );
}
